import { useCallback, useEffect, useState, type FormEvent } from 'react'
import type { IslandProps } from '../registry'
import { Badge, Button, Skeleton } from '../../shared/ui'
import { toast } from '../../lib/toast'
import {
  amPlatformOwner,
  clearZohoKeys,
  fetchAppKeys,
  saveZohoKeys,
  type AppKeysStatus,
} from '../../lib/appKeys'
import { APP_KEYS_CSS } from './appKeysStyles'

const ZOHO_CONSOLE = 'https://api-console.zoho.in'
const HOMEPAGE = 'https://shaungodinho.com'

type Phase = 'checking' | 'hidden' | 'loading' | 'ready' | 'error'

/**
 * Settings > Hunts app keys. Only the Hunts platform owner sees it
 * (`am_platform_owner()`); everyone else gets nothing rendered, heading
 * included. The owner pastes the Zoho API client id and secret once and
 * every Zoho flow (Mail and Calendar) uses them. Server env vars still win.
 */
export default function AppKeysIsland(_props: IslandProps) {
  const [phase, setPhase] = useState<Phase>('checking')
  const [status, setStatus] = useState<AppKeysStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setPhase('loading')
    try {
      setStatus(await fetchAppKeys())
      setPhase('ready')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setPhase('error')
    }
  }, [])

  useEffect(() => {
    let alive = true
    void amPlatformOwner().then((owner) => {
      if (!alive) return
      if (owner) {
        // The host ships the rail item and section hidden; only owners see them.
        document
          .querySelectorAll<HTMLElement>('[data-section="app-keys"], #set-app-keys')
          .forEach((el) => el.removeAttribute('hidden'))
        void load()
      } else setPhase('hidden')
    })
    return () => {
      alive = false
    }
  }, [load])

  if (phase === 'hidden' || phase === 'checking') return null

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!clientId.trim() || !clientSecret.trim()) {
      toast('Paste both the Client ID and the Client Secret.', 'error')
      return
    }
    setBusy(true)
    try {
      setStatus(await saveZohoKeys(clientId, clientSecret))
      setClientId('')
      setClientSecret('')
      toast('Zoho keys saved', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function clear() {
    setBusy(true)
    try {
      setStatus(await clearZohoKeys())
      toast('Zoho keys removed', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function copyRedirect(uri: string) {
    try {
      await navigator.clipboard.writeText(uri)
      toast('Copied', 'success')
    } catch {
      toast('Could not copy. Select the link and copy it.', 'error')
    }
  }

  const z = status?.zoho
  const ready = !!z && z.client_id_set && z.client_secret_set
  const redirect = status?.redirect_uri ?? ''

  return (
    <div className="ak-page">
      <style>{APP_KEYS_CSS}</style>
      <h3 className="ak-title">Hunts app keys</h3>
      <p className="ak-sub">Only you see this. Set it once and every business can connect.</p>

      {phase === 'loading' && (
        <div aria-busy="true" aria-label="Loading">
          <Skeleton width="40" />
          <Skeleton />
        </div>
      )}
      {phase === 'error' && (
        <div className="ak-card" role="alert">
          <p className="ak-error">{error ?? 'Something went wrong.'}</p>
          <div className="ak-actions">
            <Button variant="secondary" onClick={() => void load()}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {phase === 'ready' && z && (
        <section className="ak-card" aria-label="Zoho app">
          <div className="ak-card-head">
            <h4 className="ak-card-name">Zoho app (for Zoho Mail and Zoho Calendar)</h4>
            {ready ? <Badge tone="ok">Set up</Badge> : <Badge tone="warn">Not set up</Badge>}
          </div>
          {z.source === 'env' && <p className="ak-meta">Set on the server. Nothing to do here.</p>}
          {z.source === 'app' && z.client_id_hint && (
            <p className="ak-meta">Client ID ending …{z.client_id_hint}</p>
          )}

          <ol className="ak-steps">
            <li>
              Open{' '}
              <a href={ZOHO_CONSOLE} target="_blank" rel="noopener noreferrer">
                api-console.zoho.in
              </a>
              . Choose <strong>Add Client</strong> &gt; <strong>Server-based Applications</strong>.
            </li>
            <li>
              Name: <strong>Hunts</strong>. Homepage: <span className="ak-code">{HOMEPAGE}</span>.
              Redirect URI:
              <div className="ak-copy">
                <span className="ak-code" data-testid="ak-redirect">
                  {redirect}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => void copyRedirect(redirect)}
                  disabled={!redirect}
                >
                  Copy
                </Button>
              </div>
            </li>
            <li>Paste the Client ID and Client Secret below. Save.</li>
          </ol>

          <form className="ak-form" onSubmit={(e) => void save(e)}>
            <label className="ak-field">
              Client ID
              <input
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                maxLength={200}
              />
            </label>
            <label className="ak-field">
              Client Secret
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                maxLength={200}
              />
            </label>
            <div className="ak-actions">
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
              {z.source === 'app' && (
                <Button variant="danger" onClick={() => void clear()} disabled={busy}>
                  Remove keys
                </Button>
              )}
            </div>
          </form>
        </section>
      )}
    </div>
  )
}
